import { Button } from "@/components/ui/button"
import { Icon } from "@/components/Atoms/Icon"
import meetIcon from "@/assets/meet-icon.png"
import { getDate, getTime } from "@/utils/formatDate"
import { cn } from "@/utils/utils"
import { MentoriaDataType } from "@/types/mentorias"

const mentoringStatus = {
  1: { label: "Pendente", color: "bg-yellow-500/10 text-yellow-500" },
  2: { label: "Realizado", color: "bg-green-500/10 text-green-500" },
  3: { label: "Finalizado", color: "bg-green-500/10 text-green-500" },
  4: { label: "No-show", color: "bg-zinc-500/10 text-zinc-500" },
  5: { label: "Cancelado", color: "bg-red-500/10 text-red-500" },
  6: { label: "Avaliado", color: "bg-purple-500/10 text-purple-500" },
} as const

function RatingStars({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`fi ${star <= rating ? "fi-ss-star text-orange-500" : "fi-rs-star text-white/20"} flex items-center justify-center text-[15px]`}
        />
      ))}
    </div>
  )
}

interface MentoriaCardProps {
  mentoria: MentoriaDataType
  isHistory?: boolean
  isHighlighted?: boolean
  onRate: (mentoria: MentoriaDataType) => void
}

export function MentoriaCard({
  mentoria,
  isHistory = false,
  isHighlighted = false,
  onRate,
}: MentoriaCardProps) {
  return (
    <div
      className={cn(
        "group relative flex h-full flex-col justify-between rounded-2xl border p-6 transition-all duration-500",
        isHistory
          ? "border-white/5 bg-white/[0.02] opacity-75 hover:opacity-100"
          : "border-white/10 bg-gradient-to-b from-white/[0.08] to-transparent shadow-2xl hover:border-orange-500/30",
        isHighlighted && "animate-border-beam border-orange-500/50 shadow-orange-500/10"
      )}
    >
      <div className="relative z-10 flex h-full flex-col">
        <div className="mb-5 flex items-center justify-between">
          {mentoria.status_mentoria && (
            <div
              className={cn(
                "w-fit rounded-lg px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider",
                mentoringStatus[mentoria.status_mentoria].color
              )}
            >
              {mentoringStatus[mentoria.status_mentoria].label}
            </div>
          )}
          {isHistory && mentoria.mentorado_nota && <RatingStars rating={mentoria.mentorado_nota} />}
        </div>
        <div className="flex-grow space-y-4">
          <h3 className="line-clamp-2 text-lg font-bold tracking-tight text-white/90">
            {mentoria.tema_mentoria || mentoria.descricao_mentoria || "Mentoria sem título"}
          </h3>
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-sm font-bold text-orange-500">
              {mentoria.mentor_nome?.charAt(0)}
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-medium uppercase tracking-tighter text-white/40">
                Mentor
              </span>
              <span className="text-sm font-semibold text-white/80">{mentoria.mentor_nome}</span>
            </div>
          </div>
        </div>
        <div className="mt-6 space-y-4 border-t border-white/5 pt-5">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5 text-[11px] text-white/50">
                <i className="fi fi-rr-calendar text-orange-500/70" />
                {getDate(mentoria.data_hora_mentoria)}
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-white/50">
                <i className="fi fi-rr-clock text-orange-500/70" />
                {getTime(mentoria.data_hora_mentoria)}
              </div>
            </div>
            {isHistory &&
              !mentoria.mentorado_nota &&
              (mentoria.status_mentoria === 2 || mentoria.status_mentoria === 3) && (
                <Button
                  size="sm"
                  className="h-auto border border-orange-500 bg-transparent px-3 py-1.5 text-xs text-orange-500 hover:bg-orange-500 hover:text-white"
                  onClick={() => onRate(mentoria)}
                >
                  Avaliar <Icon name="star" />
                </Button>
              )}
          </div>
          {mentoria.link_mentoria && mentoria.status_mentoria === 1 && (
            <a
              href={mentoria.link_mentoria}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-orange-500 py-3 text-xs font-bold text-white shadow-lg shadow-orange-500/20 transition-all hover:scale-[1.02] hover:bg-orange-600 active:scale-95"
            >
              <img
                src={meetIcon}
                width="18"
                alt="Meet"
                className="brightness-0 invert"
              />{" "}
              Entrar na Mentoria
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
